import { SEOUL_TZ } from '@platform/datetime';
import type {
  LicenseDevice,
  LicenseDevicePlatform,
  LicenseTypeCode,
} from '../types/license-device.type';

const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;
const DAY_MS = HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE * MS_PER_SECOND;

const KST_DATE = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  month: 'long',
  day: 'numeric',
});

const KST_FULL_DATE = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

const KST_DATE_KEY = new Intl.DateTimeFormat('en-CA', {
  timeZone: SEOUL_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** 서버가 판정에 쓰는 코드. 화면 분기는 문구가 아니라 이 코드로 한다. */
export const LICENSE_DEVICE_ERROR = {
  LIMIT_EXCEEDED: 'LICENSE_DEVICE_LIMIT_EXCEEDED',
  RELEASE_QUOTA_EXCEEDED: 'LICENSE_DEVICE_RELEASE_QUOTA_EXCEEDED',
  RELEASE_REASON_REQUIRED: 'LICENSE_DEVICE_RELEASE_REASON_REQUIRED',
  ORG_LOSS_REVOKE_QUOTA_EXCEEDED: 'LICENSE_ORG_LOSS_REVOKE_QUOTA_EXCEEDED',
} as const;

/** 기기 별명 최대 길이. BE `DEVICE_NICKNAME_MAX_LENGTH`(libs/shared/license)와 같아야 한다. */
export const DEVICE_NICKNAME_MAX_LENGTH = 20;

/** 별명 입력 추천 칩. 누르면 입력란에 채워지고 이어서 고칠 수 있다 — 고정 분류가 아니다. */
export const DEVICE_NICKNAME_PRESETS = ['집', '직장', '노트북', '기타'] as const;

/** 목록 제목 — 별명 → PC 이름 → "이름 없는 PC" */
export function deviceDisplayName(
  device: Pick<LicenseDevice, 'nickname' | 'hostname'>,
): string {
  return device.nickname ?? device.hostname ?? '이름 없는 PC';
}

export const DEVICE_PLATFORM_LABEL = {
  WINDOWS: 'Windows',
  MACOS: 'macOS',
  LINUX: 'Linux',
  UNKNOWN: '기타',
} as const satisfies Record<LicenseDevicePlatform, string>;

export const LICENSE_TYPE_LABEL = {
  INDIVIDUAL: '개인',
  BUSINESS: '기업',
} as const satisfies Record<LicenseTypeCode, string>;

/** 따로 알려야 하는 라이선스 대상 — 개인·기업은 구매 주체 표시와 겹쳐 생략한다 */
const LICENSE_SEGMENT_LABEL: Partial<Record<string, string>> = {
  TRIAL: '체험판',
  GOV: '관공서',
  EDU_TEACHER: '교육·교사',
  EDU_STUDENT: '교육·학생',
  LEGACY_PERPETUAL: '영구',
};

/** 구버전 영구 라이선스 좌석 — 만료일(9999-12-31)은 저장용 값이라 화면에 날짜로 내지 않는다. */
export function isPerpetualSegment(segment?: string | null): boolean {
  return segment === 'LEGACY_PERPETUAL';
}

/** 따로 표시할 대상이면 라벨, 아니면(개인·기업·모르는 값) null */
export function specialSegmentLabel(segment?: string | null): string | null {
  return (segment && LICENSE_SEGMENT_LABEL[segment]) || null;
}

/** 좌석 요약의 대상별 내역 중 따로 알릴 것만 — 예: "체험판 3석". 없으면 null */
export function specialSegmentNote(
  segments?: readonly { segment: string; total: number }[],
): string | null {
  const parts = (segments ?? []).flatMap((s) => {
    const label = specialSegmentLabel(s.segment);
    return label && s.total > 0 ? [`${label} ${s.total}석`] : [];
  });
  return parts.length ? parts.join(' · ') : null;
}

/** UTC ISO → "10월 28일" (KST) */
export function formatKstMonthDay(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : KST_DATE.format(d);
}

/** epoch 초 → "2026. 10. 28." (KST). 0·NaN 은 "-". */
export function formatKstEpochDate(epochSeconds: number): string {
  if (!epochSeconds) return '-';
  const d = new Date(epochSeconds * MS_PER_SECOND);
  return Number.isNaN(d.getTime()) ? '-' : KST_FULL_DATE.format(d);
}

/**
 * 마지막 사용 시각 → "오늘" / "어제" / "N일 전".
 * 하루 경계는 24시간이 아니라 **KST 달력 날짜**로 자른다 — 밤 11시에 쓴 PC 가
 * 다음 날 새벽에 "오늘"로 보이면 안 된다.
 */
export function formatLastSeen(iso: string | null, now = new Date()): string {
  if (!iso) return '사용 기록 없음';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const days = Math.round(
    (Date.parse(KST_DATE_KEY.format(now)) - Date.parse(KST_DATE_KEY.format(d))) /
      DAY_MS,
  );
  if (days <= 0) return '오늘';
  if (days === 1) return '어제';
  return `${days}일 전`;
}

/** 기기 정리 화면용 — "오늘 접속" / "어제 접속" / "N일 전 접속". */
export function formatLastVisit(iso: string | null, now = new Date()): string {
  if (!iso) return '접속 기록 없음';
  const seen = formatLastSeen(iso, now);
  return seen === '-' ? seen : `${seen} 접속`;
}
